/**
 * SessionLivePanel — 70/30 split. Left: thermometer + totals + announcement
 * banner + milestones. Right: live feed sidebar (~8 most recent donations).
 *
 * Owns a MilestoneTracker + a celebration-overlay queue. Port of
 * apps/signage-tv/lib/rendering/session_live_panel.dart.
 *
 * Spec: signage-device-app-spec §3.2
 */

import { applyBranding, extractBranding, formatCurrency } from './branding.js';
import { MilestoneTracker } from './milestone-tracker.js';
import { CelebrationQueue } from './milestone-overlay.js';
import {
  ProgressCelebrationTracker,
  progressCelebrationTargetAmount,
  progressCelebrationTitle,
  resolveProgressCelebrationThresholds,
} from './progress-celebration-tracker.js';
import { renderMilestoneChip } from '../primitives/milestone-chip.js';
import { renderThermometer } from '../primitives/thermometer.js';
import { sourceBadgeLabel } from '../primitives/source-badge.js';
import { FullscreenControl } from './fullscreen-control.js';

export interface SessionLiveRenderState {
  config: Record<string, unknown> | null;
  sessionContent: Record<string, unknown> | null;
}

/**
 * Stateful per-root renderer. Kept as a class so the tracker + queue
 * survive re-renders against the same root element.
 */
export class SessionLiveRenderer {
  private readonly tracker = new MilestoneTracker();
  private readonly progressTracker = new ProgressCelebrationTracker();
  private readonly queue: CelebrationQueue;
  private primed = false;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private serverOffsetMs = 0;
  private dismissedEndedSessionKey: string | null = null;
  private readonly fullscreen: FullscreenControl;

  constructor(private readonly root: HTMLElement) {
    this.queue = new CelebrationQueue(root);
    this.fullscreen = new FullscreenControl(root);
  }

  dispose(): void {
    if (this.countdownTimer !== null) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    this.fullscreen.dispose();
    this.queue.dispose();
  }

  render(state: SessionLiveRenderState): void {
    const branding = extractBranding(state.config);
    applyBranding(this.root, branding);

    const sc = state.sessionContent ?? {};
    this.serverOffsetMs = typeof sc['server_now'] === 'string'
      ? Date.parse(sc['server_now'] as string) - Date.now()
      : 0;
    if (this.countdownTimer !== null) clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    const phase = deriveSessionDisplayPhase(sc);
    const lifecycleSessionKey = typeof sc['session_id'] === 'string'
      ? sc['session_id'] as string
      : typeof sc['session_name'] === 'string'
        ? sc['session_name'] as string
        : 'session';
    if (phase !== 'ended' && this.dismissedEndedSessionKey === lifecycleSessionKey) {
      this.dismissedEndedSessionKey = null;
    }
    if (['preparing', 'scheduled', 'countdown', 'awaiting_start'].includes(phase)) {
      this.renderScheduledPhase(sc, phase);
      this.fullscreen.render();
      return;
    }
    const raised =
      typeof sc['goal_progress_minor_units'] === 'number'
        ? (sc['goal_progress_minor_units'] as number)
        : typeof sc['credited_total_minor_units'] === 'number'
          ? (sc['credited_total_minor_units'] as number)
          : typeof sc['total_raised_minor_units'] === 'number'
            ? (sc['total_raised_minor_units'] as number)
            : 0;
    const goal = typeof sc['goal_minor_units'] === 'number' ? (sc['goal_minor_units'] as number) : 0;
    const rawPct = goal > 0 ? Math.max(0, (raised / goal) * 100) : 0;
    const pct = Math.min(1, rawPct / 100);
    const sessionName =
      typeof sc['session_name'] === 'string' ? (sc['session_name'] as string) : 'Fundraising';
    const milestones = Array.isArray(sc['milestones'])
      ? (sc['milestones'] as ReadonlyArray<Record<string, unknown>>)
      : [];
    const feed = Array.isArray(sc['feed'])
      ? (sc['feed'] as ReadonlyArray<Record<string, unknown>>)
      : [];
    const announcements = Array.isArray(sc['announcements'])
      ? (sc['announcements'] as ReadonlyArray<Record<string, unknown>>)
      : [];
    const thresholds = resolveProgressCelebrationThresholds(sc, milestones);
    const sessionKey = typeof sc['session_id'] === 'string' ? sc['session_id'] : sessionName;
    const crossedProgress = phase === 'live'
      ? this.progressTracker.diff(sessionKey, rawPct, goal, thresholds)
      : [];
    const goalMilestoneTitles = new Set(
      milestones
        .filter((milestone) => Number(milestone['amount_minor_units'] ?? milestone['amount'] ?? 0) === goal)
        .map((milestone) => (typeof milestone['title'] === 'string' ? milestone['title'].trim() : ''))
        .filter(Boolean)
    );

    // Prime the tracker from the FIRST non-empty snapshot only, so devices
    // that boot mid-session don't replay every past unlock.
    if (phase === 'live' && !this.primed && milestones.length > 0) {
      this.tracker.prime(milestones);
      this.primed = true;
    } else if (phase === 'live' && this.primed) {
      const newly = this.tracker.diff(milestones);
      for (const milestone of newly) {
        if (!goalMilestoneTitles.has(milestone.title)) {
          const presentationGoal = goal > 0
            ? goal
            : milestone.amountMinorUnits > 0
              ? milestone.amountMinorUnits
              : raised;
          const percent = goal > 0 && milestone.amountMinorUnits > 0
            ? Math.round((milestone.amountMinorUnits / goal) * 100)
            : 100;
          this.queue.enqueueProgress({
            percent,
            title: milestone.title,
            kicker: 'Milestone unlocked',
            sessionName,
            amount: formatCurrency(
              milestone.amountMinorUnits > 0 ? milestone.amountMinorUnits : presentationGoal,
              branding
            ),
            goal: formatCurrency(presentationGoal, branding),
            accent: branding.primaryColor,
          });
        }
      }
    }
    for (const percent of crossedProgress) {
      this.queue.enqueueProgress({
        percent,
        title: progressCelebrationTitle(percent),
        sessionName,
        amount: formatCurrency(progressCelebrationTargetAmount(goal, percent), branding),
        goal: formatCurrency(goal, branding),
        accent: branding.primaryColor,
      });
    }

    this.root.innerHTML = '';
    this.root.className = 'ag-app ag-session';

    const header = document.createElement('header');
    header.className = 'ag-session__header';
    this.root.appendChild(header);

    const title = document.createElement('h1');
    title.className = 'ag-session__title';
    title.textContent = sessionName;
    header.appendChild(title);

    const status = document.createElement('div');
    status.className = 'ag-session__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'off');
    const updateEndCountdown = () => renderSessionEndCountdown(status, sc, this.serverOffsetMs);
    updateEndCountdown();
    if (phase === 'live') this.countdownTimer = setInterval(updateEndCountdown, 1000);
    header.appendChild(status);

    // --- Main panel -----------------------------------------------------
    const main = document.createElement('div');
    main.className = 'ag-session__main';
    this.root.appendChild(main);

    const totals = document.createElement('div');
    totals.className = 'ag-session__totals';
    totals.textContent = `${formatCurrency(raised, branding)} of ${formatCurrency(goal, branding)}`;
    main.appendChild(totals);

    main.appendChild(renderThermometer({ percent: pct }));

    const pctLabel = document.createElement('div');
    pctLabel.className = 'ag-session__pct';
    pctLabel.textContent = `${Math.round(pct * 100)}% raised`;
    main.appendChild(pctLabel);

    // CHG-3273 / DRIFT-1246 (QA #279 follow-up) — session console announcements
    // in plain session_live BASE mode (no studio scene assigned). The backend
    // orders the list pinned-first (signage_content._fetch_session_announcements,
    // CHG-3271), so [0] is the pinned row when one exists. The banner only
    // mounts when the list is non-empty — absence leaves the pre-existing
    // layout untouched. Pure, timer-free render: safe under the change-gated
    // full innerHTML rebuild on every notify (CHG-3265).
    const banner = renderSessionAnnouncementBanner(announcements);
    if (banner) main.appendChild(banner);

    const milestoneRow = document.createElement('div');
    milestoneRow.className = 'ag-session__milestones';
    const shown = milestones.slice(0, 5);
    for (const m of shown) {
      milestoneRow.appendChild(
        renderMilestoneChip({
          title: typeof m['title'] === 'string' ? (m['title'] as string) : '',
          unlocked: m['unlocked'] === true,
        })
      );
    }
    main.appendChild(milestoneRow);

    // --- Feed sidebar ---------------------------------------------------
    const sidebar = document.createElement('aside');
    sidebar.className = 'ag-session__feed';
    this.root.appendChild(sidebar);

    const feedTitle = document.createElement('h3');
    feedTitle.textContent = 'Live Feed';
    sidebar.appendChild(feedTitle);

    if (feed.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'ag-session__feed-empty';
      empty.textContent = 'Waiting for first gift…';
      sidebar.appendChild(empty);
    } else {
      const list = document.createElement('ul');
      list.className = 'ag-session__feed-list';
      for (const entry of feed.slice(0, 8)) {
        const li = document.createElement('li');
        li.className = 'ag-session__feed-item';
        const amt = document.createElement('div');
        amt.className = 'ag-session__feed-amount';
        const cents = typeof entry['amount_minor_units'] === 'number' ? (entry['amount_minor_units'] as number) : 0;
        amt.textContent = formatCurrency(cents, branding);
        const donor = document.createElement('div');
        donor.className = 'ag-session__feed-donor';
        donor.textContent =
          typeof entry['donor_display'] === 'string' ? (entry['donor_display'] as string) : 'Donor';
        li.appendChild(amt);
        li.appendChild(donor);
        const designation =
          typeof entry['designation_name'] === 'string'
            ? (entry['designation_name'] as string).trim()
            : '';
        if (designation) {
          const chip = document.createElement('div');
          chip.className = 'ag-designation-chip';
          chip.textContent = `→ ${designation}`;
          li.appendChild(chip);
        }
        // CHG-0083-E — render a small source badge when the backend supplies it.
        // Null/undefined for historical rows predating the column — rendered without badge.
        const source = typeof entry['source'] === 'string' ? (entry['source'] as string) : null;
        const sourceLabel = sourceBadgeLabel(source);
        if (sourceLabel) {
          const badge = document.createElement('span');
          badge.className = 'ag-source-badge';
          badge.textContent = sourceLabel;
          li.appendChild(badge);
        }
        list.appendChild(li);
      }
      sidebar.appendChild(list);
    }

    // Re-attach the celebration queue's overlay node (the root was cleared).
    this.queue.reattach();
    renderSessionLifecycleOverlay(this.root, sc, state.config, {
      dismissed: phase === 'ended' && this.dismissedEndedSessionKey === lifecycleSessionKey,
      onDismiss: () => { this.dismissedEndedSessionKey = lifecycleSessionKey; },
    });
    this.fullscreen.render();
  }

  private renderScheduledPhase(sc: Record<string, unknown>, phase: string): void {
    this.root.innerHTML = '';
    this.root.className = 'ag-app ag-session ag-session-phase';
    const panel = document.createElement('main');
    panel.className = 'ag-session-phase__panel';
    const session = document.createElement('div');
    session.className = 'ag-session-phase__session';
    session.textContent = typeof sc['session_name'] === 'string' ? sc['session_name'] : 'Giving session';
    panel.appendChild(session);
    const kicker = document.createElement('div');
    kicker.className = 'ag-session-phase__kicker';
    const title = document.createElement('h1');
    title.className = 'ag-session-phase__title';
    const detail = document.createElement('p');
    detail.className = 'ag-session-phase__detail';
    if (phase === 'preparing') {
      title.textContent = 'This session is being prepared';
      detail.textContent = 'Start time coming soon';
    } else if (phase === 'scheduled') {
      kicker.textContent = 'Scheduled';
      panel.appendChild(kicker);
      title.textContent = typeof sc['localized_starts_at'] === 'string' ? sc['localized_starts_at'] : 'Scheduled';
      detail.textContent = typeof sc['timezone'] === 'string' ? sc['timezone'] : 'UTC';
    } else if (phase === 'awaiting_start') {
      title.textContent = 'Waiting for Organizer to Start';
      detail.textContent = typeof sc['localized_starts_at'] === 'string' ? sc['localized_starts_at'] : '';
    } else {
      kicker.textContent = 'Starting in';
      panel.appendChild(kicker);
      title.className += ' ag-session-phase__countdown';
      const update = () => { title.textContent = countdownValue(sc, this.serverOffsetMs); };
      update();
      this.countdownTimer = setInterval(update, 1000);
      detail.textContent = [sc['localized_starts_at'], sc['timezone']].filter((v) => typeof v === 'string' && v).join(' · ');
    }
    panel.appendChild(title);
    panel.appendChild(detail);
    this.root.appendChild(panel);
  }

}

export function deriveSessionDisplayPhase(sc: Record<string, unknown>): string {
  // Pre-lifecycle cached payloads omitted status and represented live only.
  const status = typeof sc['status'] === 'string' ? sc['status'] : 'live';
  if (status !== 'scheduled') return status;
  const startRaw = typeof sc['starts_at'] === 'string' ? sc['starts_at'] : null;
  if (!startRaw) return 'preparing';
  const serverRaw = typeof sc['server_now'] === 'string' ? sc['server_now'] : new Date().toISOString();
  const remaining = Date.parse(startRaw) - (Date.now() + Date.parse(serverRaw) - Date.now());
  if (remaining <= 0) return 'awaiting_start';
  return remaining <= 86_400_000 ? 'countdown' : 'scheduled';
}

function countdownValue(sc: Record<string, unknown>, serverOffsetMs: number): string {
  const start = Date.parse(String(sc['starts_at'] ?? ''));
  const seconds = Math.max(0, Math.ceil((start - (Date.now() + serverOffsetMs)) / 1000));
  if (seconds === 0) return 'Waiting for Organizer to Start';
  return [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map((part) => String(part).padStart(2, '0')).join(':');
}

export type SessionEndCountdown =
  | { kind: 'live'; label: 'Live' }
  | { kind: 'inactive'; label: 'Paused' | 'Ended' }
  | { kind: 'countdown'; seconds: number; urgent: boolean; label: string }
  | { kind: 'ending'; label: 'Session ending…' };

export function sessionEndCountdown(
  sc: Record<string, unknown>,
  serverOffsetMs: number,
  nowMs = Date.now()
): SessionEndCountdown {
  const status = typeof sc['status'] === 'string' ? sc['status'] : 'live';
  if (status === 'paused') return { kind: 'inactive', label: 'Paused' };
  if (status === 'ended') return { kind: 'inactive', label: 'Ended' };
  const endRaw = typeof sc['ends_at'] === 'string' ? sc['ends_at'] : null;
  if (status !== 'live' || !endRaw) return { kind: 'live', label: 'Live' };
  const endMs = Date.parse(endRaw);
  if (!Number.isFinite(endMs)) return { kind: 'live', label: 'Live' };
  const seconds = Math.max(0, Math.ceil((endMs - (nowMs + serverOffsetMs)) / 1000));
  if (seconds > 120) return { kind: 'live', label: 'Live' };
  if (seconds === 0) return { kind: 'ending', label: 'Session ending…' };
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return {
    kind: 'countdown',
    seconds,
    urgent: seconds <= 30,
    label: `Ends in ${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`,
  };
}

function renderSessionEndCountdown(
  node: HTMLElement,
  sc: Record<string, unknown>,
  serverOffsetMs: number
): void {
  const value = sessionEndCountdown(sc, serverOffsetMs);
  node.className = 'ag-session__status';
  if (value.kind === 'inactive') node.classList.add('ag-session__status--inactive');
  if (value.kind !== 'live') node.classList.add('ag-session__status--countdown');
  if (value.kind === 'ending' || (value.kind === 'countdown' && value.urgent)) {
    node.classList.add('ag-session__status--urgent');
  }
  node.textContent = value.label;
  node.setAttribute('aria-label', value.label);
}

const lifecycleOverlayTimers = new WeakMap<HTMLElement, ReturnType<typeof setInterval>>();

export function renderSessionLifecycleOverlay(
  root: HTMLElement,
  sc: Record<string, unknown> | null,
  config: Record<string, unknown> | null = null,
  options: { dismissed?: boolean; onDismiss?: () => void } = {}
): void {
  const oldTimer = lifecycleOverlayTimers.get(root);
  if (oldTimer !== undefined) clearInterval(oldTimer);
  lifecycleOverlayTimers.delete(root);
  if (!sc) return;
  const phase = deriveSessionDisplayPhase(sc);
  if (phase === 'live') return;
  if (phase === 'ended' && options.dismissed) return;
  const overlay = document.createElement('div');
  overlay.className = `ag-session-lifecycle ag-session-lifecycle--${phase}`;
  overlay.setAttribute('role', phase === 'ended' ? 'dialog' : 'status');
  if (phase === 'ended') {
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ag-session-ended-title');
  }
  const card = document.createElement('div');
  card.className = 'ag-session-lifecycle__card';
  if (phase === 'preparing') {
    card.innerHTML = '<h2>This session is being prepared</h2><p>Start time coming soon</p>';
  } else if (phase === 'scheduled') {
    const phaseLabel = document.createElement('p');
    phaseLabel.className = 'ag-session-lifecycle__phase-label';
    phaseLabel.textContent = 'Scheduled';
    const title = document.createElement('h2');
    title.textContent = typeof sc['localized_starts_at'] === 'string' ? sc['localized_starts_at'] : 'Scheduled';
    const detail = document.createElement('p'); detail.textContent = String(sc['timezone'] ?? 'UTC');
    card.append(phaseLabel, title, detail);
  } else if (phase === 'countdown') {
    const phaseLabel = document.createElement('p');
    phaseLabel.className = 'ag-session-lifecycle__phase-label';
    phaseLabel.textContent = 'Starting in';
    const title = document.createElement('h2');
    const offset = typeof sc['server_now'] === 'string' ? Date.parse(sc['server_now'] as string) - Date.now() : 0;
    const update = () => { title.textContent = countdownValue(sc, offset); };
    update();
    lifecycleOverlayTimers.set(root, setInterval(update, 1000));
    const detail = document.createElement('p'); detail.textContent = [sc['localized_starts_at'], sc['timezone']].filter(Boolean).join(' · ');
    card.append(phaseLabel, title, detail);
  } else if (phase === 'awaiting_start') {
    card.innerHTML = '<h2>Waiting for Organizer to Start</h2>';
  } else if (phase === 'paused') {
    card.innerHTML = '<h2>Giving paused</h2><p>Current totals and activity remain visible while giving is paused.</p>';
  } else {
    card.tabIndex = -1;
    const branding = extractBranding(config);
    const cash = Number(sc['cash_raised_minor_units'] ?? sc['donation_raised_minor_units'] ?? 0);
    const pledged = Number(sc['pledged_minor_units'] ?? sc['total_pledged_minor_units'] ?? 0);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ag-session-lifecycle__close';
    close.setAttribute('aria-label', 'Close final summary');
    close.title = 'Close final summary';
    close.textContent = '×';
    close.addEventListener('click', () => {
      options.onDismiss?.();
      overlay.remove();
    });
    const endedTitle = document.createElement('h2');
    endedTitle.id = 'ag-session-ended-title';
    endedTitle.className = 'ag-session-lifecycle__ended-title';
    endedTitle.textContent = 'The session has ended';
    const endedSummaryIntro = document.createElement('p');
    endedSummaryIntro.className = 'ag-session-lifecycle__ended-summary';
    endedSummaryIntro.textContent = 'Here is the final summary';
    const totals = document.createElement('div');
    totals.className = 'ag-session-lifecycle__totals';
    totals.append(
      renderEndedTotal('Total raised', formatCurrency(cash, branding)),
      renderEndedTotal('Total pledged', formatCurrency(pledged, branding)),
    );
    const thanks = document.createElement('p');
    thanks.className = 'ag-session-lifecycle__thanks';
    thanks.textContent = 'Thank you for your generosity';
    card.append(close, endedTitle, endedSummaryIntro, totals, thanks);
  }
  overlay.appendChild(card);
  root.appendChild(overlay);
  if (phase === 'ended') card.focus({ preventScroll: true });
}

function renderEndedTotal(labelText: string, formatted: string): HTMLElement {
  const section = document.createElement('section');
  section.className = 'ag-session-lifecycle__total-card';
  const label = document.createElement('p');
  label.className = 'ag-session-lifecycle__label';
  label.textContent = labelText;
  const amount = document.createElement('div');
  amount.className = 'ag-session-lifecycle__amount';
  if (formatted.length > 16) amount.classList.add('ag-session-lifecycle__amount--extra-long');
  else if (formatted.length > 10) amount.classList.add('ag-session-lifecycle__amount--long');
  amount.textContent = formatted;
  section.append(label, amount);
  return section;
}

/**
 * Compact announcement banner for the session_live main column.
 *
 * CHG-3273 / DRIFT-1246 — parity with the TV panel's _AnnouncementBanner
 * (session_live_panel.dart). Shows the TOP announcement only (backend is
 * pinned-first) plus a "+N more" hint; rotation is intentionally out of
 * scope. Session console rows are title-less (title === '', body carries
 * the text — CHG-3271), so like studio-scene renderAnnouncements the banner
 * falls back to a generic label and renders the body text; a titled
 * (org/message-shaped) row shows its title as a heading. Returns null when
 * the list is empty so the caller mounts nothing (no layout jump).
 */
export function renderSessionAnnouncementBanner(
  announcements: ReadonlyArray<Record<string, unknown>>
): HTMLElement | null {
  if (announcements.length === 0) return null;
  // Backend orders pinned-first already; the find() is a defensive guard so
  // a pinned row still wins if a cached/older payload isn't pre-sorted.
  const top = announcements.find((a) => a['pinned'] === true) ?? announcements[0];
  const pinned = top['pinned'] === true;

  const banner = document.createElement('div');
  banner.className = pinned
    ? 'ag-session__announcement ag-session__announcement--pinned'
    : 'ag-session__announcement';

  const label = document.createElement('div');
  label.className = 'ag-session__announcement-label';
  label.textContent = pinned ? 'Pinned announcement' : 'Announcement';
  banner.appendChild(label);

  const rawTitle = typeof top['title'] === 'string' ? (top['title'] as string).trim() : '';
  const rawBody = typeof top['body'] === 'string' ? (top['body'] as string).trim() : '';
  const bodyText =
    rawBody || (typeof top['message'] === 'string' ? (top['message'] as string).trim() : '');

  if (rawTitle) {
    const heading = document.createElement('strong');
    heading.className = 'ag-session__announcement-title';
    heading.textContent = rawTitle;
    banner.appendChild(heading);
  }
  if (bodyText && bodyText !== rawTitle) {
    const p = document.createElement('p');
    p.className = 'ag-session__announcement-body';
    p.textContent = bodyText;
    banner.appendChild(p);
  }

  if (announcements.length > 1) {
    const rest = announcements.length - 1;
    const more = document.createElement('div');
    more.className = 'ag-session__announcement-more';
    more.textContent = rest === 1 ? '+1 more announcement' : `+${rest} more announcements`;
    banner.appendChild(more);
  }
  return banner;
}
